// Shapes mirrored from apps/api/src (routes/*.ts, moderation/settings.ts, db/migrations/001_init.sql).

export const ACTIONS = ['allow', 'flag', 'block_message', 'block_message_and_user'] as const;
export type Action = (typeof ACTIONS)[number];

export const SEVERITIES = ['none', 'low', 'medium', 'high', 'critical'] as const;
export type Severity = (typeof SEVERITIES)[number];

export type Role = 'owner' | 'admin' | 'viewer';
export const ROLE_RANK: Record<Role, number> = { viewer: 0, admin: 1, owner: 2 };
export const canManage = (actor: Role, target: Role) => ROLE_RANK[actor] >= ROLE_RANK[target];

export const CATEGORY_KEY_RE = /^[a-z][a-z0-9_]{1,40}$/;

export interface User {
  id: string;
  email: string;
  name: string;
  role: Role;
}
export interface Organization {
  id: string;
  name: string;
}
export interface Me {
  user: User;
  organization: Organization;
}

export interface ApiKeyCreated {
  id: string;
  name: string;
  prefix: string;
  created_at: string;
  key: string;
}
export interface SignupResponse {
  user: User;
  organization: Organization;
  api_key: ApiKeyCreated;
}
export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

export interface Rule {
  id: string;
  name: string;
  enabled: boolean;
  when: {
    categories?: string[];
    min_severity?: Severity;
    min_risk?: number;
    fragmented?: boolean;
    repeat_offender?: { count: number; window_minutes: number };
  };
  action: Action;
}

export interface ModerationSettings {
  thresholds: { flag: number; block_message: number; block_message_and_user: number };
  block_user_min_severity: Severity;
  category_actions: Record<string, Action>;
  custom_categories: { key: string; description: string }[];
  rules: Rule[];
  custom_instructions: string;
  context: { same_user_messages: number; other_user_messages: number; window_minutes: number };
  fragment_detection: boolean;
  block_messages_from_blocked_users: boolean;
  cache_repeated_content: boolean;
}

export interface Usage {
  plan: string;
  limits: {
    monthly_messages: number;
    overage: boolean;
    messages_per_second: number;
    max_terms_chars: number;
    max_context_messages: number;
    max_decision_streams: number;
  };
  period: { start: string; end: string };
  messages_accepted: number;
  messages_remaining: number | null;
  overage_messages: number;
  messages_moderated: number;
  messages_failed: number;
  reprocessed: number;
  cache_hits: number;
  model_calls: number;
  model_input_tokens: number;
  terms: { chars: number; chars_used: number; limit: number };
}

export interface Settings {
  moderation: ModerationSettings;
  categories: Record<string, string>;
  webhook_url: string | null;
  webhook_enabled: boolean;
  webhook_secret?: string;
  jev: { model: string; mock: boolean };
  /** `days` in effect; `setting` is the organization's choice (null: the plan maximum, `max_days`). */
  retention: { days: number; setting: number | null; max_days: number };
  updated_at?: string;
}

export type PolicyStatus = 'pending' | 'ready' | 'failed';
export interface Policy {
  id: string;
  kind: 'url' | 'document' | 'text';
  name: string;
  source_url: string | null;
  filename: string | null;
  mime_type: string | null;
  char_count: number;
  status: PolicyStatus;
  error: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  content_text?: string | null;
}

export type MessageStatus = 'queued' | 'processing' | 'done' | 'failed';
export type ReviewStatus = 'confirmed' | 'overridden' | null;

export interface MessageListItem {
  id: string;
  external_id: string;
  conversation_id: string | null;
  end_user_id: string;
  end_user_name: string | null;
  content: string;
  status: MessageStatus;
  action: Action | null;
  /** The model's own decision; differs from `action` when a reviewer overrode it. */
  model_action: Action | null;
  flags: string[];
  category: string | null;
  severity: Severity | null;
  risk: number | null;
  received_at: string;
  processed_at: string | null;
  review_status: ReviewStatus;
  related_message_ids: string[];
}

export interface Scores {
  toxic: number;
  terms_violation: number;
  fragmented: number | null;
  category_confidence: number | null;
}

export interface MessageFull extends MessageListItem {
  org_id: string;
  metadata: Record<string, unknown>;
  sent_at: string;
  scores: Scores | null;
  reasons: string[] | null;
  jev_model: string | null;
  error: string | null;
  attempts: number;
  reviewed_by: string | null;
  reviewer_email: string | null;
  reviewed_at: string | null;
}

export interface ContextMessage {
  id: string;
  external_id: string;
  end_user_id: string;
  content: string;
  action: Action | null;
  sent_at: string;
}

export interface ClientContextMessage {
  message_id?: string;
  user_id: string;
  content: string;
  timestamp?: string;
}

export type DeliveryStatus = 'pending' | 'success' | 'failed';
export interface WebhookDelivery {
  id: string;
  message_id?: string | null;
  event: string;
  url?: string;
  status: DeliveryStatus;
  attempts: number;
  last_status_code: number | null;
  last_error: string | null;
  created_at: string;
  delivered_at: string | null;
  payload?: unknown;
}

export interface EndUser {
  org_id: string;
  external_id: string;
  display_name: string | null;
  status: 'active' | 'blocked';
  message_count: number;
  violation_count: number;
  last_violation_at: string | null;
  blocked_at: string | null;
  blocked_reason: string | null;
  first_seen_at: string;
  last_seen_at: string;
}

export interface MessageDetail {
  message: MessageFull;
  client_context: ClientContextMessage[];
  context: ContextMessage[];
  webhook_deliveries: WebhookDelivery[];
  end_user: EndUser | null;
}

export interface Stats {
  days: number;
  totals: {
    total: number;
    allow: number;
    flag: number;
    block_message: number;
    block_message_and_user: number;
    in_queue: number;
    failed: number;
    avg_latency_ms: number | null;
  };
  blocked_users: number;
  daily: { day: string; action: Action | null; n: number }[];
  categories: { category: string; n: number }[];
  top_offenders: { end_user_id: string; end_user_name: string | null; violations: number }[];
}

export interface TeamUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  created_at: string;
  last_login_at: string | null;
}

export interface WebhookTestResult {
  id: string;
  status: DeliveryStatus;
  last_status_code: number | null;
  last_error: string | null;
}
